import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../api';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  CogIcon,
  BoltIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  TrashIcon,
  ScaleIcon
} from '@heroicons/react/24/outline';

function Settings() {
  const { projectId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [generatingPersonality, setGeneratingPersonality] = useState(false);

  // Form state
  const [aiProvider, setAiProvider] = useState('ollama');
  const [modelName, setModelName] = useState('');
  const [customModelName, setCustomModelName] = useState('');
  const [isCustomModel, setIsCustomModel] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [temperature, setTemperature] = useState(0.7);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [showThinking, setShowThinking] = useState(false);
  const [extendedThinking, setExtendedThinking] = useState(false);
  const [availableModels, setAvailableModels] = useState([]);
  const [communityConstitution, setCommunityConstitution] = useState([]);
  const [newRule, setNewRule] = useState('');

  useEffect(() => {
    loadProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    loadModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiProvider]);

  const loadProject = async () => {
    try {
      const response = await api.get(`/api/projects/${projectId}`);
      const data = response.data;
      setAiProvider(data.ai_provider || 'ollama');
      setApiKey(data.api_key || '');
      setTemperature(data.temperature || 0.7);
      setSystemPrompt(data.system_prompt || '');
      setShowThinking(data.show_thinking || false);
      setExtendedThinking(data.extended_thinking || false);
      setCommunityConstitution(data.community_constitution || []);

      // Load models first to check if current model is custom
      const modelsRes = await api.get(`/api/models/${data.ai_provider || 'ollama'}`);
      const models = modelsRes.data.models || [];
      setAvailableModels(models);

      const isPresetModel = models.some(m => m.name === data.model_name && m.name !== 'custom');
      if (isPresetModel) {
        setModelName(data.model_name || '');
        setIsCustomModel(false);
      } else {
        setModelName('custom');
        setCustomModelName(data.model_name || '');
        setIsCustomModel(true);
      }
    } catch (err) {
      setError('Failed to load project');
      console.error('Error loading project:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadModels = async () => {
    try {
      const response = await api.get(`/api/models/${aiProvider}`);
      setAvailableModels(response.data.models || []);
      if (response.data.models?.length > 0 && !modelName) {
        setModelName(response.data.models[0].name);
      }
    } catch (err) {
      console.error('Error loading models:', err);
    }
  };

  const handleModelChange = (e) => {
    const value = e.target.value;
    setModelName(value);
    if (value === 'custom') {
      setIsCustomModel(true);
    } else {
      setIsCustomModel(false);
      setCustomModelName('');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);

    // Use custom model name if "custom" is selected
    const finalModelName = isCustomModel ? customModelName : modelName;

    if (!finalModelName) {
      setError('Please enter a model name');
      setSaving(false);
      return;
    }

    try {
      await api.put(`/api/projects/${projectId}`, {
        ai_provider: aiProvider,
        model_name: finalModelName,
        api_key: apiKey || null,
        temperature: temperature,
        system_prompt: systemPrompt,
        show_thinking: showThinking,
        extended_thinking: extendedThinking,
        community_constitution: communityConstitution
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addConstitutionRule = () => {
    if (newRule.trim()) {
      setCommunityConstitution([...communityConstitution, newRule.trim()]);
      setNewRule('');
    }
  };

  const removeConstitutionRule = (index) => {
    setCommunityConstitution(communityConstitution.filter((_, i) => i !== index));
  };

  const handleRuleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addConstitutionRule();
    }
  };

  const handleGeneratePersonality = async () => {
    if (!apiKey && aiProvider !== 'ollama') {
      setError('API key required to generate personality');
      return;
    }

    setGeneratingPersonality(true);
    setError(null);

    try {
      const response = await api.post(`/api/projects/${projectId}/generate-personality`, null, {
        params: {
          provider: aiProvider === 'ollama' ? 'anthropic' : aiProvider,
          api_key: apiKey || undefined
        }
      });
      setSystemPrompt(response.data.personality);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate personality');
    } finally {
      setGeneratingPersonality(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="h-12 w-12 border-2 border-green-500/30 border-t-green-500 rounded-full animate-spin mx-auto"></div>
        <p className="mt-4 text-gray-400 font-mono text-sm">
          <span className="text-green-400">$</span> loading settings...<span className="animate-pulse">_</span>
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Back Button */}
      <Link
        to={`/console/projects/${projectId}`}
        className="inline-flex items-center text-gray-400 hover:text-green-400 font-mono text-sm mb-6 group"
      >
        <ArrowLeftIcon className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform" />
        <span className="text-green-400">$</span> cd ../dashboard
      </Link>

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white font-mono flex items-center space-x-2">
            <span className="text-green-400">$</span>
            <span>./settings</span>
          </h2>
          <p className="text-gray-500 font-mono text-sm mt-1">
            Configure AI model and personality
          </p>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-6 bg-red-900/30 border border-red-500/50 rounded-lg p-4 font-mono text-sm flex items-center space-x-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
          <span className="text-red-300">{error}</span>
        </div>
      )}

      {saved && (
        <div className="mb-6 bg-green-900/30 border border-green-500/50 rounded-lg p-4 font-mono text-sm flex items-center space-x-2">
          <CheckCircleIcon className="h-5 w-5 text-green-400" />
          <span className="text-green-300">Settings saved successfully</span>
        </div>
      )}

      {/* Settings Form */}
      <div className="space-y-6">
        {/* AI Provider Section */}
        <div className="bg-gray-900 rounded-lg border border-gray-700 p-6">
          <h3 className="text-sm font-mono text-cyan-400 mb-2"># ai_provider</h3>
          <p className="text-xs text-gray-500 mb-4">
            Choose where your AI runs. Ollama is free and private (runs locally). Cloud providers offer more powerful models but require API keys and send data to their servers.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-mono text-gray-300 mb-2">--provider</label>
              <select
                value={aiProvider}
                onChange={(e) => setAiProvider(e.target.value)}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white font-mono focus:ring-2 focus:ring-green-500"
              >
                <option value="ollama">ollama (local, free)</option>
                <option value="openai">openai (cloud)</option>
                <option value="anthropic">anthropic (cloud)</option>
              </select>
              <p className="mt-1 text-xs text-gray-500 font-mono">
                {aiProvider === 'ollama' && '# Runs on your hardware. Free forever, 100% private.'}
                {aiProvider === 'openai' && '# Cloud API. Costs per query, data sent to OpenAI.'}
                {aiProvider === 'anthropic' && '# Cloud API. Costs per query, data sent to Anthropic.'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-mono text-gray-300 mb-2">--model</label>
              {availableModels.length > 0 ? (
                <select
                  value={modelName}
                  onChange={handleModelChange}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white font-mono focus:ring-2 focus:ring-green-500"
                >
                  {availableModels.map((model) => (
                    <option key={model.name} value={model.name}>
                      {model.display || model.name}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={modelName}
                  onChange={(e) => setModelName(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white font-mono focus:ring-2 focus:ring-green-500"
                />
              )}
              <p className="mt-1 text-xs text-gray-500 font-mono">
                # Larger models (8B+) give better answers but need more RAM/VRAM
              </p>
              {isCustomModel && (
                <div className="mt-3">
                  <label className="block text-sm font-mono text-gray-400 mb-2">--custom-model-name</label>
                  <input
                    type="text"
                    value={customModelName}
                    onChange={(e) => setCustomModelName(e.target.value)}
                    placeholder={aiProvider === 'ollama' ? 'e.g., qwen2:7b' : aiProvider === 'openai' ? 'e.g., gpt-4-0125-preview' : 'e.g., claude-3-opus-20240229'}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white font-mono placeholder-gray-500 focus:ring-2 focus:ring-green-500"
                  />
                  <p className="mt-1 text-xs text-gray-500 font-mono">
                    # Enter the exact model identifier
                  </p>
                </div>
              )}
            </div>
          </div>

          {(aiProvider === 'openai' || aiProvider === 'anthropic') && (
            <div className="mt-4">
              <label className="block text-sm font-mono text-gray-300 mb-2">--api-key</label>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={aiProvider === 'anthropic' ? 'sk-ant-...' : 'sk-...'}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white font-mono placeholder-gray-500 focus:ring-2 focus:ring-green-500"
              />
              <p className="mt-1 text-xs text-gray-500 font-mono">
                # Get your API key from {aiProvider === 'anthropic' ? 'console.anthropic.com' : 'platform.openai.com'}
              </p>
            </div>
          )}
        </div>

        {/* Model Parameters Section */}
        <div className="bg-gray-900 rounded-lg border border-gray-700 p-6">
          <h3 className="text-sm font-mono text-cyan-400 mb-2"># model_parameters</h3>
          <p className="text-xs text-gray-500 mb-4">
            Fine-tune how your AI responds. These settings affect response style and quality.
          </p>

          <div className="space-y-6">
            {/* Temperature */}
            <div>
              <label className="block text-sm font-mono text-gray-300 mb-2">
                --temperature <span className="text-cyan-400">{temperature}</span>
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={temperature}
                onChange={(e) => setTemperature(parseFloat(e.target.value))}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-green-500"
              />
              <div className="flex justify-between text-xs text-gray-500 font-mono mt-1">
                <span>focused (0.0)</span>
                <span>creative (1.0)</span>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Controls randomness in responses. Lower values (0.0-0.3) give consistent, factual answers—best for civic Q&A.
                Higher values (0.7-1.0) produce more varied, creative responses.
                Recommended: <span className="text-green-400">0.3-0.5</span> for community assistants.
              </p>
            </div>

            {/* Thinking Options */}
            <div className="pt-4 border-t border-gray-700">
              <label className="block text-sm font-mono text-gray-300 mb-3">--thinking-options</label>

              <div className="space-y-3">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showThinking}
                    onChange={(e) => setShowThinking(e.target.checked)}
                    className="w-4 h-4 mt-0.5 rounded border-gray-600 bg-gray-800 text-green-500 focus:ring-green-500 focus:ring-offset-gray-900"
                  />
                  <div>
                    <span className="font-mono text-sm text-gray-300">--show-thinking</span>
                    <p className="text-xs text-gray-500 mt-1">
                      Display the AI's reasoning process before answering. Helps users understand how answers are formed.
                    </p>
                  </div>
                </label>

                {aiProvider === 'anthropic' && (
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={extendedThinking}
                      onChange={(e) => setExtendedThinking(e.target.checked)}
                      className="w-4 h-4 mt-0.5 rounded border-gray-600 bg-gray-800 text-green-500 focus:ring-green-500 focus:ring-offset-gray-900"
                    />
                    <div>
                      <span className="font-mono text-sm text-gray-300">--extended-thinking</span>
                      <span className="ml-2 px-1.5 py-0.5 text-xs bg-purple-500/20 text-purple-400 rounded">beta</span>
                      <p className="text-xs text-gray-500 mt-1">
                        Enable Claude's extended thinking mode for complex questions. Takes longer but provides deeper analysis.
                      </p>
                    </div>
                  </label>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Personality Section */}
        <div className="bg-gray-900 rounded-lg border border-gray-700 p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-mono text-cyan-400"># system_prompt</h3>
            <button
              onClick={handleGeneratePersonality}
              disabled={generatingPersonality}
              className="px-3 py-1 text-xs font-mono bg-purple-500/20 text-purple-400 border border-purple-500/50 rounded hover:bg-purple-500/30 transition-colors flex items-center space-x-1 disabled:opacity-50"
            >
              {generatingPersonality ? (
                <>
                  <ArrowPathIcon className="h-3 w-3 animate-spin" />
                  <span>generating...</span>
                </>
              ) : (
                <>
                  <BoltIcon className="h-3 w-3" />
                  <span>auto-generate</span>
                </>
              )}
            </button>
          </div>
          <p className="text-xs text-gray-500 mb-4">
            Define your AI's personality, tone, and area of expertise. This shapes how it introduces itself and responds to questions.
            Click "auto-generate" to create a personality based on your location.
          </p>

          <textarea
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            rows={12}
            placeholder="Define your AI's personality, tone, and behavior..."
            className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white font-mono text-sm placeholder-gray-500 focus:ring-2 focus:ring-green-500 resize-none"
          />
          <p className="mt-2 text-xs text-gray-500 font-mono">
            # Tip: Include your town name, describe the AI's role, and set the tone (friendly, formal, etc.)
          </p>
        </div>

        {/* Community Constitution Section */}
        <div className="bg-gray-900 rounded-lg border border-orange-500/30 p-6">
          <div className="flex items-center space-x-2 mb-4">
            <ScaleIcon className="h-5 w-5 text-orange-400" />
            <h3 className="text-sm font-mono text-orange-400"># community_constitution</h3>
          </div>

          <p className="text-gray-400 text-sm mb-4">
            Define ethical guidelines and constraints that your AI must always follow.
            These rules take precedence over other instructions.
          </p>

          {/* Existing Rules */}
          {communityConstitution.length > 0 && (
            <div className="space-y-2 mb-4">
              {communityConstitution.map((rule, index) => (
                <div
                  key={index}
                  className="flex items-start space-x-3 bg-gray-800 rounded-lg p-3 border border-gray-700 group"
                >
                  <span className="text-orange-400 font-mono text-sm flex-shrink-0">
                    {String(index + 1).padStart(2, '0')}.
                  </span>
                  <p className="text-gray-300 text-sm flex-1">{rule}</p>
                  <button
                    onClick={() => removeConstitutionRule(index)}
                    className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition-opacity"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add New Rule */}
          <div className="flex space-x-2">
            <input
              type="text"
              value={newRule}
              onChange={(e) => setNewRule(e.target.value)}
              onKeyPress={handleRuleKeyPress}
              placeholder="e.g., Always recommend contacting town hall for official permits..."
              className="flex-1 px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white font-mono text-sm placeholder-gray-500 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
            <button
              onClick={addConstitutionRule}
              disabled={!newRule.trim()}
              className="px-4 py-3 bg-orange-500/20 text-orange-400 border border-orange-500/50 rounded-lg hover:bg-orange-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <PlusIcon className="h-5 w-5" />
            </button>
          </div>

          <p className="mt-3 text-xs text-gray-500 font-mono">
            # Press Enter or click + to add a rule
          </p>

          {/* Example Rules */}
          {communityConstitution.length === 0 && (
            <div className="mt-4 p-4 bg-gray-800/50 rounded-lg border border-dashed border-gray-700">
              <p className="text-xs text-gray-500 font-mono mb-2"># Example rules:</p>
              <ul className="space-y-1 text-xs text-gray-500">
                <li>- Always recommend contacting town hall for official matters</li>
                <li>- Never provide legal advice - suggest consulting an attorney</li>
                <li>- Encourage residents to attend public meetings for civic engagement</li>
                <li>- Clarify that information may be outdated and verify with official sources</li>
                <li>- Respect privacy and never ask for personal identifying information</li>
              </ul>
            </div>
          )}
        </div>

        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-green-500 text-gray-900 py-3 px-4 rounded-lg font-mono font-semibold hover:bg-green-400 disabled:bg-gray-700 disabled:text-gray-500 transition-colors flex items-center justify-center space-x-2"
        >
          {saving ? (
            <>
              <ArrowPathIcon className="h-5 w-5 animate-spin" />
              <span>Saving...</span>
            </>
          ) : (
            <>
              <CogIcon className="h-5 w-5" />
              <span>$ ./save --config</span>
              <span className="animate-pulse">_</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default Settings;
